import { useState, useRef, useEffect } from 'react';
import { Box, useTheme } from '@mui/material';
import { MessageContextMenu } from '@client/components';
import { PyrenzMessageBox } from '@client/theme';
import { useChatStore } from '@client/store';

interface MessageBoxProps {
  index: number;
  isGenerating: boolean;
  isLastMessage: boolean;
  onRegenerate: () => void;
  onRemove: () => void;
  handleSpeak: () => void;
  handleCopy: () => void;
  onGenerateImage: (messageId: number) => void;
  onEditClick: (
    messageId: number,
    currentMessage: string,
    type: 'user' | 'char'
  ) => void;
}

export function MessageBox({
  index,
  isGenerating,
  isLastMessage,
  onRegenerate,
  onRemove,
  handleSpeak,
  handleCopy,
  onGenerateImage,
  onEditClick,
}: MessageBoxProps) {
  const theme = useTheme();
  const { user, character, messages } = useChatStore();
  const msg = messages[index];
  const role = msg?.type;
  
  const numericId = msg?.id ?? -1;
  const isValidId = numericId > 0;
  const isFirstMessage = msg?.is_first === true;
  
  const [isEditing, setIsEditing] = useState(false);
  const [localEditedMessage, setLocalEditedMessage] = useState(msg?.text || '');
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => setLocalEditedMessage(msg?.text || ''), [msg?.text]);

  const handleMessageBoxClick = (e: React.MouseEvent) => {
    if (!isFirstMessage && !isGenerating && !isEditing && isValidId) {
      setMenuPosition({ top: e.clientY, left: e.clientX });
    }
  };

  const handleEditClickFromMenu = () => {
    setIsEditing(true);
    setMenuPosition(null);
  };

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        e.target instanceof Node &&
        !menuRef.current.contains(e.target)
      ) {
        setMenuPosition(null);
      }
    };
    if (menuPosition) document.addEventListener('mousedown', onOutside);
    else document.removeEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [menuPosition]);

  if (!msg) return null;

  const displayText = msg.chunks?.join('') || msg.text || '';

  const canShowMenu = !isFirstMessage && !isGenerating && !isEditing && isValidId;

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      justifyContent={role === 'user' ? 'flex-end' : 'flex-start'}
      sx={{ mb: 3, width: '100%', position: 'relative', px: { xs: 1, sm: 2 } }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: '85%', sm: '70%', md: '60%' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: role === 'user' ? 'flex-end' : 'flex-start',
          transform: role === 'user' ? 'translateX(-10px)' : 'translateX(10px)',
        }}
      >
        <PyrenzMessageBox
          onClick={handleMessageBoxClick}
          role={role}
          msg={msg}
          content={displayText}
          userAvatar={role === 'user' ? user?.user_avatar || '' : ''}
          charAvatar={role === 'char' ? character?.profile_image || '' : ''}
          isEditing={isEditing}
          localEditedMessage={localEditedMessage}
          onChange={(e) => setLocalEditedMessage(e.target.value)}
          onSaveEdit={() => {
            if (isValidId) {
              onEditClick(numericId, localEditedMessage, role!);
            }
            setIsEditing(false);
          }}
          onCancelEdit={() => setIsEditing(false)}
        />
      </Box>

      {menuPosition && canShowMenu && (
        <Box
          ref={menuRef}
          sx={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
            zIndex: 1300,
          }}
        >
          <MessageContextMenu
            msg={msg}
            onRegenerate={onRegenerate}
            onRemove={onRemove}
            handleSpeak={handleSpeak}
            handleCopy={handleCopy}
            onGenerateImage={() => isValidId && onGenerateImage(numericId)}
            onEditClick={handleEditClickFromMenu}
            onClose={() => setMenuPosition(null)}
          />
        </Box>
      )}
    </Box>
  );
}